import { Table } from "@tanstack/react-table";
import { RefreshCcw, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCampaignFilterActions } from "../ducks/filter/hooks/actions";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { DataTableViewOptions } from "./data-table-view-options";
import { CampaignTypeFilter } from "@/components/campaigns/campaign-type-filter";
import { CampaignDateFilter } from "@/components/campaigns/campaign-date-filter";
import { CampaignStatusFilter } from "@/components/campaigns/campaign-status-filter";
import { CampaignStoreFilter } from "@/components/campaigns/campaign-store-filter";
import { CampaignSearchInput } from "./campaign-search-input";
import { CampaignType } from "@/@types/campaign";
import { SearchType } from "../ducks/filter/types";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  countByType?: Record<string, number>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function DataTableToolbar<TData>({
  table,
  countByType,
  onRefresh,
  isRefreshing = false,
}: DataTableToolbarProps<TData>) {
  const navigate = useNavigate();
  const { updateFilter } = useCampaignFilterActions();
  const { filters } = useFilterState();

  const handleRefresh = () => {
    onRefresh?.();
  };

  const handleTypesChange = (types: CampaignType[]) => {
    // Reset searchType if it becomes invalid for the new types
    const currentSearchType = filters.searchType || "name";
    const hasSku = types.some(t => t === CampaignType.PRO014 || t === CampaignType.PRO017);
    const hasDept = types.some(t => t === CampaignType.PRO013);

    if (currentSearchType === "sku" && !hasSku) {
      updateFilter({ key: "searchType", value: "name" });
      updateFilter({ key: "search", value: "" });
    }
    if (currentSearchType === "department" && !hasDept) {
      updateFilter({ key: "searchType", value: "name" });
      updateFilter({ key: "search", value: "" });
    }

    updateFilter({ key: "campaignTypes", value: types });
  };

  const [localSearch, setLocalSearch] = useState(filters.search || "");

  const handleSearchTypeChange = useCallback((type: SearchType) => {
    updateFilter({ key: "searchType", value: type });
    setLocalSearch("");
    updateFilter({ key: "search", value: "" });
  }, [updateFilter]);

  const handleSearchApply = useCallback(() => {
    const trimmed = localSearch.trim();
    updateFilter({ key: "search", value: trimmed || undefined });
  }, [localSearch, updateFilter]);

  const handleSearchClear = useCallback(() => {
    setLocalSearch("");
    updateFilter({ key: "search", value: undefined });
  }, [updateFilter]);

  const handleDateRangeChange = (range?: { from?: Date; to?: Date }) => {
    updateFilter({
      key: "startDateFrom",
      value: range?.from ? format(range.from, "yyyy-MM-dd") : undefined,
    });
    updateFilter({
      key: "endDateTo",
      value: range?.to ? format(range.to, "yyyy-MM-dd") : undefined,
    });
  };

  const handleInEffectChange = (value: boolean) => {
    updateFilter({ key: "inEffect", value: value || undefined });
  };

  // STORY-NEW-043: Handler para filtro de loja (multi-seleção)
  const handleStoresChange = (storeCodes: number[]) => {
    updateFilter({ key: "stores", value: storeCodes.length > 0 ? storeCodes : undefined });
  };

  const dateRange = filters.startDateFrom || filters.endDateTo
    ? {
      from: filters.startDateFrom ? parseISO(filters.startDateFrom) : undefined,
      to: filters.endDateTo ? parseISO(filters.endDateTo) : undefined,
    }
    : undefined;

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex flex-1 items-center gap-2 flex-wrap">
        <CampaignSearchInput
          searchType={filters.searchType || "name"}
          searchValue={localSearch}
          campaignTypes={filters.campaignTypes || [CampaignType.PRO013]}
          onSearchTypeChange={handleSearchTypeChange}
          onSearchValueChange={setLocalSearch}
          onSearch={handleSearchApply}
          onClear={handleSearchClear}
        />
        <CampaignTypeFilter
          selectedTypes={filters.campaignTypes || [CampaignType.PRO013]}
          onTypesChange={handleTypesChange}
          countByType={countByType}
        />
        <CampaignDateFilter
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />
        <CampaignStatusFilter
          inEffect={filters.inEffect || false}
          onInEffectChange={handleInEffectChange}
        />
        <CampaignStoreFilter
          selectedStores={filters.stores ?? []}
          onApply={handleStoresChange}
        />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          className="h-8"
          onClick={() => navigate("/planejamento-comercial/gestao-campanhas/create")}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Campanha
        </Button>
        <Button
          variant="outline"
          className="h-8"
          onClick={handleRefresh}
          disabled={isRefreshing}
          aria-busy={isRefreshing}
        >
          <RefreshCcw className={cn("h-4 w-4 mr-2 transition-transform", isRefreshing && "animate-spin")} />
          {isRefreshing ? "Atualizando..." : "Atualizar"}
        </Button>
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}
